/**
 * A value that the helpers accept as a date: a `Date`, a timestamp in milliseconds, or a string.
 * Strings are read by {@link DateHelper.toDate}.
 */
export type DateInput = Date | number | string;

/** Units of time of the helpers. A week starts on the day passed by the caller, an ISO week starts on Monday. */
export type DateUnit = "year" | "month" | "week" | "isoWeek" | "day" | "hour" | "minute" | "second" | "millisecond";

const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = 60 * MS_PER_SECOND;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const MS_PER_DAY = 24 * MS_PER_HOUR;
const MS_PER_WEEK = 7 * MS_PER_DAY;

/**
 * ISO 8601 date, with optional time and offset: `2026-10-14`, `2026-10-14T10:30`, `2026-10-14 10:30:15.250`,
 * `2026-10-14T10:30:15+02:00`, `2026-10-14T08:30:15Z`. Groups: year, month, day, hour, minute, second, fraction, offset.
 */
const ISO_DATE_TIME =
  /^\s*(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2})(?::(\d{2})(?::(\d{2})(?:[.,](\d+))?)?)?)?\s*(Z|[+-]\d{2}(?::?\d{2})?)?\s*$/i;

/** Tokens of the fixed patterns, longest first so that `yyyy` wins over `yy`. */
const PATTERN_TOKENS = /'[^']*'|yyyy|yy|MM|M|dd|d|HH|H|mm|m|ss|s|SSS|xxx|xx/g;

/** What each token of a pattern matches when a string follows the pattern exactly. */
const TOKEN_SHAPES: Record<string, string> = {
  yyyy: String.raw`\d{4}`,
  yy: String.raw`\d{2}`,
  MM: String.raw`\d{2}`,
  M: String.raw`\d{1,2}`,
  dd: String.raw`\d{2}`,
  d: String.raw`\d{1,2}`,
  HH: String.raw`\d{2}`,
  H: String.raw`\d{1,2}`,
  mm: String.raw`\d{2}`,
  m: String.raw`\d{1,2}`,
  ss: String.raw`\d{2}`,
  s: String.raw`\d{1,2}`,
  SSS: String.raw`\d{3}`,
  xxx: String.raw`(?:Z|[+-]\d{2}:\d{2})`,
  xx: String.raw`(?:Z|[+-]\d{4})`,
};

/**
 * Date and time functions of the application: reading the strings of the API, writing them in the fixed formats
 * the API expects, and calendar arithmetic. Local time is the default everywhere, as the API exchanges local times.
 *
 * The patterns use the letters of Unicode, Angular and .NET (`dd-MM-yyyy HH:mm:ss`), the same the API uses to parse:
 * `yyyy` `yy` year, `MM` `M` month, `dd` `d` day, `HH` `H` hour, `mm` `m` minute, `ss` `s` second,
 * `SSS` milliseconds, `xxx` offset as `+02:00`, `xx` offset as `+0200`, text between single quotes as it is.
 * Names of months and days depend on the language and are written by the locale formatting, not here.
 */
export class DateHelper {
  /**
   * Reads a date. A string in ISO 8601 without an offset is a local time, and a date alone is local midnight
   * (`new Date("2026-10-14")` would read UTC midnight instead). A string with `Z` or an offset is that instant.
   * Any other string is read by `new Date(string)`. `undefined` is the current time, `null` an invalid date.
   *
   * @param value value to read
   * @returns a new `Date`, invalid when the value cannot be read
   */
  static toDate(value?: DateInput | null): Date {
    if (value === undefined) {
      return new Date();
    }
    if (value === null) {
      return new Date(NaN);
    }
    if (value instanceof Date) {
      return new Date(value.getTime());
    }
    if (typeof value === "number") {
      return new Date(value);
    }
    const iso = ISO_DATE_TIME.exec(value);
    if (!iso) {
      return new Date(value);
    }
    const [, year, month, day, hour, minute, second, fraction, offset] = iso;
    const milliseconds = fraction ? Math.floor(Number((fraction + "00").substring(0, 3))) : 0;
    const parts = [Number(year), Number(month) - 1, Number(day), Number(hour ?? 0), Number(minute ?? 0), Number(second ?? 0), milliseconds];
    if (!DateHelper.isValidParts(parts)) {
      return new Date(NaN);
    }
    if (!offset) {
      return DateHelper.localDate(parts);
    }
    const utc = Date.UTC(parts[0], parts[1], parts[2], parts[3], parts[4], parts[5], parts[6]);
    return new Date(utc - DateHelper.offsetToMinutes(offset) * MS_PER_MINUTE);
  }

  /**
   * Reads a date like {@link DateHelper.toDate}, but a string in ISO 8601 without an offset is a UTC time,
   * for the fields the API writes in UTC without saying so.
   *
   * @param value value to read
   * @returns a new `Date`, invalid when the value cannot be read
   */
  static toDateUtc(value?: DateInput | null): Date {
    if (typeof value === "string" && ISO_DATE_TIME.test(value) && !ISO_DATE_TIME.exec(value)?.[8]) {
      return DateHelper.toDate(value.trim() + "Z");
    }
    return DateHelper.toDate(value);
  }

  /**
   * Reads a string written with a fixed pattern, such as `14/10/2026 10:30` with `dd/MM/yyyy HH:mm`.
   * The numbers are taken in the order of the pattern and the separators may differ. A missing year is the current
   * one, a missing date is today, a missing day is the first of the month, missing time fields are zero.
   * `yy` is a year between 1969 and 2068. A `Date` is taken as it is and the pattern is ignored; a number is read as
   * the string of its digits, as moment did.
   *
   * @param value string to read
   * @param pattern pattern of the string
   * @returns a new `Date`, invalid when a field is out of range or missing from the string
   */
  static parse(value: DateInput | null | undefined, pattern: string): Date {
    if (value === null || value === undefined) {
      return new Date(NaN);
    }
    if (value instanceof Date) {
      return DateHelper.toDate(value);
    }
    const fields: Record<string, number> = {};
    let offsetMinutes: number | null = null;
    let rest = String(value);
    let foundAny = false;
    for (const token of pattern.match(PATTERN_TOKENS) ?? []) {
      if (token.startsWith("'")) {
        continue;
      }
      if (token === "xxx" || token === "xx") {
        const match = /Z|[+-]\d{2}:?\d{2}/i.exec(rest);
        if (match) {
          offsetMinutes = DateHelper.offsetToMinutes(match[0]);
          rest = rest.substring(match.index + match[0].length);
        }
        continue;
      }
      const digits = token === "yyyy" ? "\\d{1,4}" : token === "SSS" ? "\\d{1,3}" : "\\d{1,2}";
      const match = new RegExp(digits).exec(rest);
      if (!match) {
        continue;
      }
      foundAny = true;
      rest = rest.substring(match.index + match[0].length);
      const number = Number(match[0]);
      switch (token) {
        case "yyyy":
          fields.year = number;
          break;
        case "yy":
          fields.year = number + (number > 68 ? 1900 : 2000);
          break;
        case "MM":
        case "M":
          fields.month = number - 1;
          break;
        case "dd":
        case "d":
          fields.day = number;
          break;
        case "HH":
        case "H":
          fields.hour = number;
          break;
        case "mm":
        case "m":
          fields.minute = number;
          break;
        case "ss":
        case "s":
          fields.second = number;
          break;
        case "SSS":
          fields.millisecond = Number((match[0] + "00").substring(0, 3));
          break;
      }
    }
    if (!foundAny) {
      return new Date(NaN);
    }
    const now = new Date();
    const today = [now.getFullYear(), now.getMonth(), now.getDate()];
    const date = [fields.year, fields.month, fields.day];
    // Leading missing date fields come from today, the remaining ones default to the start of the period
    for (let i = 0; i < 3 && date[i] === undefined; i++) {
      date[i] = today[i];
    }
    const parts = [date[0], date[1] ?? 0, date[2] ?? 1, fields.hour ?? 0, fields.minute ?? 0, fields.second ?? 0, fields.millisecond ?? 0];
    if (!DateHelper.isValidParts(parts)) {
      return new Date(NaN);
    }
    if (offsetMinutes === null) {
      return DateHelper.localDate(parts);
    }
    const utc = Date.UTC(parts[0], parts[1], parts[2], parts[3], parts[4], parts[5], parts[6]);
    return new Date(utc - offsetMinutes * MS_PER_MINUTE);
  }

  /**
   * Reads a string only when it follows the pattern exactly, with the same number of digits and the same
   * separators: `1-2-2026` is invalid for `dd-MM-yyyy`, while {@link DateHelper.parse} would accept it.
   *
   * @param value string to read
   * @param pattern pattern the string must follow
   * @returns a new `Date`, invalid when the string does not follow the pattern or a field is out of range
   */
  static parseStrict(value: string | null | undefined, pattern: string): Date {
    if (typeof value !== "string" || !DateHelper.patternToRegExp(pattern).test(value)) {
      return new Date(NaN);
    }
    return DateHelper.parse(value, pattern);
  }

  /**
   * Reads a string that can come in more than one pattern: the first pattern the string follows exactly is used,
   * otherwise the first one that gives a valid date. `14-10-2026` with `["dd-MM-yyyy", "yyyy-MM-dd"]` uses the
   * first, `2026-10-14` the second.
   *
   * @param value string to read
   * @param patterns accepted patterns, in order of preference
   * @returns a new `Date`, invalid when no pattern gives a valid date
   */
  static parseAny(value: DateInput | null | undefined, patterns: string[]): Date {
    if (typeof value === "string") {
      const exact = patterns.find((pattern) => DateHelper.patternToRegExp(pattern).test(value.trim()));
      if (exact) {
        return DateHelper.parse(value, exact);
      }
    }
    for (const pattern of patterns) {
      const date = DateHelper.parse(value, pattern);
      if (DateHelper.isValid(date)) {
        return date;
      }
    }
    return new Date(NaN);
  }

  /**
   * Writes a date with a fixed pattern, for the API, keys and file names: the result never depends on the
   * language. For the text shown to the user use the locale formatting instead.
   *
   * @param value date to write
   * @param pattern pattern, for example `dd-MM-yyyy HH:mm:ss`
   * @param utc true to write the UTC time and a zero offset instead of the local ones
   * @returns the formatted string, or `"Invalid date"` when the date is invalid
   */
  static format(value: DateInput, pattern: string, utc: boolean = false): string {
    const date = DateHelper.toDate(value);
    if (!DateHelper.isValid(date)) {
      return "Invalid date";
    }
    const get = utc
      ? [date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds(), date.getUTCMilliseconds()]
      : [date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds()];
    const offset = utc ? 0 : -date.getTimezoneOffset();
    return pattern.replace(PATTERN_TOKENS, (token) => {
      switch (token) {
        case "yyyy":
          return DateHelper.pad(get[0], 4);
        case "yy":
          return DateHelper.pad(get[0] % 100, 2);
        case "MM":
          return DateHelper.pad(get[1] + 1, 2);
        case "M":
          return String(get[1] + 1);
        case "dd":
          return DateHelper.pad(get[2], 2);
        case "d":
          return String(get[2]);
        case "HH":
          return DateHelper.pad(get[3], 2);
        case "H":
          return String(get[3]);
        case "mm":
          return DateHelper.pad(get[4], 2);
        case "m":
          return String(get[4]);
        case "ss":
          return DateHelper.pad(get[5], 2);
        case "s":
          return String(get[5]);
        case "SSS":
          return DateHelper.pad(get[6], 3);
        case "xxx":
        case "xx": {
          const sign = offset < 0 ? "-" : "+";
          const hours = DateHelper.pad(Math.floor(Math.abs(offset) / 60), 2);
          const minutes = DateHelper.pad(Math.abs(offset) % 60, 2);
          return sign + hours + (token === "xxx" ? ":" : "") + minutes;
        }
        default:
          // Quoted text: the quotes go away, two single quotes are one quote
          return token.length === 2 ? "'" : token.substring(1, token.length - 1);
      }
    });
  }

  /**
   * Adds an amount of time. Days, weeks, months and years move the calendar and keep the time of day, also across
   * a change of daylight saving time; a month that does not have the day uses its last day (31 January plus one
   * month is 28 or 29 February). Hours and smaller units add exact milliseconds.
   *
   * @param value starting date
   * @param amount amount to add, negative to go back
   * @param unit unit of the amount
   * @returns a new `Date`
   */
  static add(value: DateInput, amount: number, unit: DateUnit): Date {
    const date = DateHelper.toDate(value);
    switch (unit) {
      case "year":
        return DateHelper.addMonths(date, amount * 12);
      case "month":
        return DateHelper.addMonths(date, amount);
      case "week":
      case "isoWeek":
        date.setDate(date.getDate() + amount * 7);
        return date;
      case "day":
        date.setDate(date.getDate() + amount);
        return date;
      case "hour":
        return new Date(date.getTime() + amount * MS_PER_HOUR);
      case "minute":
        return new Date(date.getTime() + amount * MS_PER_MINUTE);
      case "second":
        return new Date(date.getTime() + amount * MS_PER_SECOND);
      case "millisecond":
        return new Date(date.getTime() + amount);
    }
  }

  /**
   * Subtracts an amount of time, with the rules of {@link DateHelper.add}.
   *
   * @param value starting date
   * @param amount amount to subtract
   * @param unit unit of the amount
   * @returns a new `Date`
   */
  static subtract(value: DateInput, amount: number, unit: DateUnit): Date {
    return DateHelper.add(value, -amount, unit);
  }

  /**
   * Start of the period that contains the date, in local time: midnight for a day, the first day of the month at
   * midnight for a month, and so on.
   *
   * @param value date
   * @param unit period
   * @param firstDayOfWeek first day of a `week`, 0 is Sunday and 1 is Monday; see {@link DateHelper.firstDayOfWeek}
   * @returns a new `Date`
   */
  static startOf(value: DateInput, unit: DateUnit, firstDayOfWeek: number = 1): Date {
    const date = DateHelper.toDate(value);
    switch (unit) {
      case "year":
        return new Date(date.getFullYear(), 0, 1);
      case "month":
        return new Date(date.getFullYear(), date.getMonth(), 1);
      case "week":
      case "isoWeek": {
        const first = unit === "isoWeek" ? 1 : firstDayOfWeek;
        const back = (date.getDay() - first + 7) % 7;
        return new Date(date.getFullYear(), date.getMonth(), date.getDate() - back);
      }
      case "day":
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
      // Hours and smaller units are cut on the timestamp with the offset of the date, so that in the hour repeated
      // when daylight saving time ends the result keeps the offset of the date
      case "hour": {
        const time = date.getTime();
        return new Date(time - DateHelper.mod(time - date.getTimezoneOffset() * MS_PER_MINUTE, MS_PER_HOUR));
      }
      case "minute":
        return new Date(date.getTime() - DateHelper.mod(date.getTime(), MS_PER_MINUTE));
      case "second":
        return new Date(date.getTime() - DateHelper.mod(date.getTime(), MS_PER_SECOND));
      case "millisecond":
        return date;
    }
  }

  /**
   * Last millisecond of the period that contains the date, in local time.
   *
   * @param value date
   * @param unit period
   * @param firstDayOfWeek first day of a `week`, 0 is Sunday and 1 is Monday
   * @returns a new `Date`
   */
  static endOf(value: DateInput, unit: DateUnit, firstDayOfWeek: number = 1): Date {
    if (unit === "millisecond") {
      return DateHelper.toDate(value);
    }
    const start = DateHelper.startOf(value, unit, firstDayOfWeek);
    return new Date(DateHelper.add(start, 1, unit).getTime() - 1);
  }

  /**
   * Difference `value - other` in whole units, rounded towards zero. Days and weeks count calendar days, so a day
   * with a change of daylight saving time still counts as one. Months and years count calendar months.
   *
   * @param value first date
   * @param other date to subtract
   * @param unit unit of the result
   * @param precise true to keep the decimals
   * @returns the difference, negative when `value` comes before `other`
   */
  static diff(value: DateInput, other: DateInput, unit: DateUnit = "millisecond", precise: boolean = false): number {
    const a = DateHelper.toDate(value);
    const b = DateHelper.toDate(other);
    let result: number;
    switch (unit) {
      case "year":
        result = DateHelper.monthDiff(a, b) / 12;
        break;
      case "month":
        result = DateHelper.monthDiff(a, b);
        break;
      case "week":
      case "isoWeek":
      case "day": {
        // The change of offset between the two dates is removed, so that calendar days count as whole days
        const offsetChange = (a.getTimezoneOffset() - b.getTimezoneOffset()) * MS_PER_MINUTE;
        result = (a.getTime() - b.getTime() - offsetChange) / (unit === "day" ? MS_PER_DAY : MS_PER_WEEK);
        break;
      }
      case "hour":
        result = (a.getTime() - b.getTime()) / MS_PER_HOUR;
        break;
      case "minute":
        result = (a.getTime() - b.getTime()) / MS_PER_MINUTE;
        break;
      case "second":
        result = (a.getTime() - b.getTime()) / MS_PER_SECOND;
        break;
      case "millisecond":
        result = a.getTime() - b.getTime();
        break;
    }
    if (precise) {
      return result;
    }
    return (result < 0 ? Math.ceil(result) : Math.floor(result)) || 0;
  }

  /**
   * Tells whether two dates fall in the same period, or are the same instant when no unit is given.
   *
   * @param value first date
   * @param other second date
   * @param unit period to compare
   * @param firstDayOfWeek first day of a `week`, 0 is Sunday and 1 is Monday
   */
  static isSame(value: DateInput, other: DateInput, unit: DateUnit = "millisecond", firstDayOfWeek: number = 1): boolean {
    const time = DateHelper.toDate(other).getTime();
    return DateHelper.startOf(value, unit, firstDayOfWeek).getTime() <= time && time <= DateHelper.endOf(value, unit, firstDayOfWeek).getTime();
  }

  /**
   * Tells whether the period of `value` ends before `other`, or whether `value` comes before `other` when no unit
   * is given.
   */
  static isBefore(value: DateInput, other: DateInput, unit: DateUnit = "millisecond", firstDayOfWeek: number = 1): boolean {
    return DateHelper.endOf(value, unit, firstDayOfWeek).getTime() < DateHelper.toDate(other).getTime();
  }

  /**
   * Tells whether the period of `value` starts after `other`, or whether `value` comes after `other` when no unit
   * is given.
   */
  static isAfter(value: DateInput, other: DateInput, unit: DateUnit = "millisecond", firstDayOfWeek: number = 1): boolean {
    return DateHelper.toDate(other).getTime() < DateHelper.startOf(value, unit, firstDayOfWeek).getTime();
  }

  /** {@link DateHelper.isSame} or {@link DateHelper.isBefore}. */
  static isSameOrBefore(value: DateInput, other: DateInput, unit: DateUnit = "millisecond", firstDayOfWeek: number = 1): boolean {
    return DateHelper.isSame(value, other, unit, firstDayOfWeek) || DateHelper.isBefore(value, other, unit, firstDayOfWeek);
  }

  /** {@link DateHelper.isSame} or {@link DateHelper.isAfter}. */
  static isSameOrAfter(value: DateInput, other: DateInput, unit: DateUnit = "millisecond", firstDayOfWeek: number = 1): boolean {
    return DateHelper.isSame(value, other, unit, firstDayOfWeek) || DateHelper.isAfter(value, other, unit, firstDayOfWeek);
  }

  /**
   * Tells whether a date is between two others, both included.
   *
   * @param value date to check
   * @param from start of the interval
   * @param to end of the interval
   */
  static isBetweenIncluded(value: DateInput, from: DateInput, to: DateInput): boolean {
    const time = DateHelper.toDate(value).getTime();
    return DateHelper.toDate(from).getTime() <= time && time <= DateHelper.toDate(to).getTime();
  }

  /**
   * Tells whether a value is a valid date.
   *
   * @param value value to check, read by {@link DateHelper.toDate}
   */
  static isValid(value: DateInput): boolean {
    return !isNaN(DateHelper.toDate(value).getTime());
  }

  /**
   * Day of the week from 1 (Monday) to 7 (Sunday), as in ISO 8601; `NaN` for an invalid date.
   *
   * @param value date
   */
  static isoWeekday(value: DateInput): number {
    const day = DateHelper.toDate(value).getDay();
    return isNaN(day) ? NaN : day || 7;
  }

  /**
   * First day of the week in a language of the application: Sunday (0) in English, Monday (1) in Italian, German,
   * French and Spanish.
   *
   * @param language language code, such as `it` or `en-US`
   */
  static firstDayOfWeek(language: string): number {
    return language?.toLowerCase().startsWith("en") ? 0 : 1;
  }

  /** Number of days of a month; `month` goes from 0 (January) to 11. */
  static daysInMonth(year: number, month: number): number {
    return new Date(year, month + 1, 0).getDate();
  }

  private static addMonths(date: Date, months: number): Date {
    const result = new Date(date.getTime());
    if (!months) {
      return result;
    }
    const target = date.getMonth() + months;
    const year = date.getFullYear() + Math.floor(target / 12);
    const month = DateHelper.mod(target, 12);
    // setMonth accepts a month out of 0-11 and moves the year
    result.setMonth(target, Math.min(date.getDate(), DateHelper.daysInMonth(year, month)));
    return result;
  }

  /** Calendar months from `b` to `a` with the fraction of the last month, as moment computes it. */
  private static monthDiff(a: Date, b: Date): number {
    if (a.getDate() < b.getDate()) {
      return -DateHelper.monthDiff(b, a);
    }
    const whole = (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth());
    const anchor = DateHelper.addMonths(a, whole).getTime();
    let adjust: number;
    if (b.getTime() - anchor < 0) {
      const previous = DateHelper.addMonths(a, whole - 1).getTime();
      adjust = (b.getTime() - anchor) / (anchor - previous);
    } else {
      const next = DateHelper.addMonths(a, whole + 1).getTime();
      adjust = (b.getTime() - anchor) / (next - anchor);
    }
    return -(whole + adjust) || 0;
  }

  /** Year, month (0-11), day, hours, minutes, seconds and milliseconds of a real local date, 24:00:00 included. */
  private static isValidParts(parts: number[]): boolean {
    const [year, month, day, hour, minute, second, millisecond] = parts;
    if (month < 0 || month > 11 || day < 1 || day > DateHelper.daysInMonth(year, month)) {
      return false;
    }
    if (hour === 24) {
      return minute === 0 && second === 0 && millisecond === 0;
    }
    return hour >= 0 && hour < 24 && minute >= 0 && minute < 60 && second >= 0 && second < 60;
  }

  private static localDate(parts: number[]): Date {
    const date = new Date(parts[0], parts[1], parts[2], parts[3], parts[4], parts[5], parts[6]);
    // Years from 0 to 99 would become 1900-1999
    if (parts[0] < 100) {
      date.setFullYear(parts[0]);
    }
    return date;
  }

  private static offsetToMinutes(offset: string): number {
    if (offset.toUpperCase() === "Z") {
      return 0;
    }
    const digits = offset.replace(":", "");
    const minutes = Number(digits.substring(1, 3)) * 60 + Number(digits.substring(3, 5) || 0);
    return offset.startsWith("-") ? -minutes : minutes;
  }

  /** Regular expression that a string written exactly with the pattern follows. */
  private static patternToRegExp(pattern: string): RegExp {
    const escape = (text: string): string => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    let source = "";
    let last = 0;
    for (const match of pattern.matchAll(PATTERN_TOKENS)) {
      const token = match[0];
      source += escape(pattern.substring(last, match.index));
      if (token.startsWith("'")) {
        source += escape(token.length === 2 ? "'" : token.substring(1, token.length - 1));
      } else {
        source += TOKEN_SHAPES[token];
      }
      last = (match.index ?? 0) + token.length;
    }
    return new RegExp("^" + source + escape(pattern.substring(last)) + "$");
  }

  /** Remainder that is never negative, also for dates before 1970. */
  private static mod(value: number, divisor: number): number {
    return ((value % divisor) + divisor) % divisor;
  }

  private static pad(value: number, length: number): string {
    return String(value).padStart(length, "0");
  }
}
